const createOption = (value, label) => ({ value, label })

const recipeVocabularies = {
    // Labels are authoritative for the admin surface (Catalan operative language).
    // Public surfaces (es/en/ca) must resolve labels via the `vocabularies` i18n namespace
    // in app/src/i18n/locales, never from these constants directly.
    statuses: [
        createOption('draft', 'Esborrany'),
        createOption('published', 'Publicada'),
        createOption('archived', 'Arxivada')
    ],
    mealTypes: [
        createOption('desayuno', 'Esmorzar'),
        createOption('comida', 'Dinar'),
        createOption('merienda', 'Berenar'),
        createOption('cena', 'Sopar')
    ],
    // Diet tags (recipe_diets junction) — used as the single multi-select control
    // in the admin editor ("Estilo de dieta"). Only the four canonical styles
    // requested by Ops are allowed across authoring and preference validation.
    dietTypes: [
        createOption('vegetariana', 'Vegetariana'),
        createOption('vegana', 'Vegana'),
        createOption('omnivora', 'Omnívora'),
        createOption('pescetariana', 'Pescetariana')
    ],
    units: [
        createOption('g', 'g'),
        createOption('kg', 'kg'),
        createOption('ml', 'ml'),
        createOption('l', 'l'),
        createOption('ud', 'ud')
    ],
    difficultyLevels: [
        createOption('easy', 'Fàcil'),
        createOption('medium', 'Mitjana'),
        createOption('hard', 'Alta')
    ],
    cookingMethods: [
        createOption('raw', 'Cru'),
        createOption('boiled', 'Bullit'),
        createOption('steamed', 'Al vapor'),
        createOption('pot', 'Olla'),
        createOption('sauteed', 'Saltejat'),
        createOption('baked', 'Al forn'),
        createOption('grilled', 'A la planxa'),
        createOption('stewed', 'Guisat'),
        createOption('blended', 'Triturat'),
        createOption('microwaved', 'Microones'),
        createOption('air_fried', 'Fregidora d\'aire'),
        createOption('poached', 'Escalfat')
    ],
    dishTypes: [
        createOption('breakfast', 'Esmorzar'),
        createOption('starter', 'Entrant'),
        createOption('main', 'Principal'),
        createOption('side', 'Acompanyament'),
        createOption('salad', 'Amanida'),
        createOption('soup', 'Sopa o crema'),
        createOption('snack', 'Snack'),
        createOption('dessert', 'Postres'),
        createOption('fruit', 'Fruita'),
        createOption('drink', 'Beguda')
    ],
    seasonalities: [
        createOption('all_year', 'Tot l\'any'),
        createOption('spring', 'Primavera'),
        createOption('summer', 'Estiu'),
        createOption('autumn', 'Tardor'),
        createOption('winter', 'Hivern')
    ],
    // Recipe "key words" multi-select (admin authoring). Mirrors the CHECK
    // constraint on recipes.key_words (text[]); no exclusivity rule. Labels are
    // authoritative for admin (Catalan); public surfaces must resolve via the
    // `vocabularies` i18n namespace, never from these constants directly.
    keyWords: [
        createOption('familiar', 'Familiar'),
        createOption('infantil', 'Infantil'),
        createOption('festive_meal', 'Àpat festiu'),
        createOption('finger_food', 'Ideal per pica-pica')
    ],
    satietyLevels: [
        createOption('1', 'Molt lleugera'),
        createOption('2', 'Moderadament completa'),
        createOption('3', 'Molt saciant')
    ],
    proteinOrigins: [
        createOption('none', 'Cap'),
        createOption('egg', 'Ou'),
        createOption('meat', 'Carn blanca'),
        createOption('red_meat', 'Carn vermella'),
        createOption('legume', 'Llegum'),
        createOption('nuts_seeds', 'Fruits secs / llavors'),
        createOption('tofu', 'Tofu / soja'),
        createOption('white_fish', 'Peix blanc'),
        createOption('oily_fish', 'Peix blau'),
        createOption('molluscs', 'Mol·luscs'),
        createOption('dairy', 'Làctics'),
        createOption('mixed', 'Mixte')
    ],
    fatTypes: [
        createOption('none', 'Cap'),
        createOption('olive_oil', 'Oli d\'oliva'),
        createOption('seed_oil', 'Oli de llavors'),
        createOption('dairy', 'Làctics'),
        createOption('nuts_seeds', 'Fruits secs i llavors'),
        createOption('avocado', 'Alvocat'),
        createOption('oily_fish', 'Peix blau'),
        createOption('white_fish', 'Peix blanc'),
        createOption('animal_fat', 'Greix animal'),
        createOption('egg', 'Ou'),
        createOption('coconut', 'Coco / greix vegetal sòlid'),
        createOption('mixed', 'Mixte')
    ],
    carbSources: [
        createOption('none', 'Cap'),
        createOption('grain', 'Cereal'),
        createOption('legume', 'Llegum'),
        createOption('tuber', 'Tubèrcle'),
        createOption('fruit', 'Fruita'),
        createOption('vegetable', 'Verdura / Hortalissa'),
        createOption('mixed', 'Mixte')
    ],
    // Course roles a recipe can fill inside a multi-course meal (lunch / dinner).
    // Mirrors the CHECK constraint on `recipes.course_role` and `menu_recipes.course`
    // (see migration 20260527000000_add_fruit_course.sql).
    courseRoles: [
        createOption('starter', 'Entrant'),
        createOption('main', 'Principal'),
        createOption('dessert', 'Postres'),
        createOption('fruit', 'Fruita')
    ],
    recipeAllergenFlags: [
        createOption('containsGluten', 'Gluten'),
        createOption('containsCrustaceans', 'Crustacis'),
        createOption('containsEgg', 'Ou'),
        createOption('containsFish', 'Peix'),
        createOption('containsPeanuts', 'Cacauets'),
        createOption('containsSoy', 'Soja'),
        createOption('containsDairy', 'Làctics'),
        createOption('containsTreeNuts', 'Fruits secs'),
        createOption('containsCelery', 'Api'),
        createOption('containsMustard', 'Mostassa'),
        createOption('containsSesame', 'Sèsam'),
        createOption('containsSulfites', 'Sulfits'),
        createOption('containsMolluscs', 'Mol·luscs'),
        createOption('containsLupins', 'Tramussos')
    ]
}

const recipeImportAliasMaps = {
    difficultyLevels: {
        facil: 'easy',
        baja: 'easy',
        media: 'medium',
        dificil: 'hard',
        alta: 'hard'
    },
    cookingMethods: {
        crudo: 'raw',
        horno: 'baked',
        olla: 'pot',
        sarten: 'sauteed',
        sartén: 'sauteed',
        pochado: 'poached',
        escalfado: 'poached'
    },
    dishTypes: {
        entrante: 'starter',
        principal: 'main'
    },
    seasonalities: {
        invierno: 'winter',
        otoño: 'autumn',
        otono: 'autumn',
        primavera: 'spring',
        todo_el_ano: 'all_year',
        'todo_el_año': 'all_year',
        verano: 'summer'
    },
    satietyLevels: {
        bajo: '1',
        medio: '2',
        alto: '3'
    },
    dietTypes: {
        vegetariana: 'vegetariana',
        vegana: 'vegana',
        omnivora: 'omnivora',
        pescetariana: 'pescetariana'
    },
    units: {
        g: 'g',
        gr: 'g',
        ml: 'ml',
        kg: 'kg',
        kilo: 'kg',
        kilos: 'kg',
        kilogramo: 'kg',
        kilogramos: 'kg',
        l: 'l',
        lt: 'l',
        litro: 'l',
        litros: 'l',
        ud: 'ud',
        uds: 'ud',
        unidad: 'ud',
        unidades: 'ud',
        rodaja: 'ud',
        rodajas: 'ud'
    }
}

function normalizeImportAliasKey(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .trim()
}

function getRecipeImportAliasValue(aliasKey, value) {
    const aliasMap = recipeImportAliasMaps[aliasKey] || {}
    return aliasMap[normalizeImportAliasKey(value)] || null
}

function getRecipeVocabularyValues(vocabularyKey) {
    return (recipeVocabularies[vocabularyKey] || []).map(({ value }) => value)
}

function getRecipeVocabularyOptions(vocabularyKey) {
    return recipeVocabularies[vocabularyKey] || []
}

function isRecipeVocabularyValue(vocabularyKey, value) {
    return getRecipeVocabularyValues(vocabularyKey).includes(value)
}

export {
    recipeVocabularies,
    recipeImportAliasMaps,
    getRecipeImportAliasValue,
    getRecipeVocabularyValues,
    getRecipeVocabularyOptions,
    isRecipeVocabularyValue
}

export default recipeVocabularies
